import GENRE_MAP from './genreMap';

const STORE_DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;
const STOREFRONT_TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;

const STOREFRONT_URL = `https://${STORE_DOMAIN}/api/2024-01/graphql.json`;

async function shopifyFetch(query, variables = {}) {
  const res = await fetch(STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) {
    throw new Error(`Shopify API error: ${res.status} ${res.statusText}`);
  }
  const json = await res.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }
  return json.data;
}

// Fetch all products (paginated)
export async function fetchProducts() {
  const query = `
    query($cursor: String) {
      products(first: 250, after: $cursor) {
        pageInfo {
          hasNextPage
        }
        edges {
          cursor
          node {
            id
            title
            handle
            description
            tags
            productType
            images(first: 30) {
              edges {
                node {
                  url
                  altText
                }
              }
            }
            options {
              name
              values
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  availableForSale
                  image {
                    url
                    altText
                  }
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  `;
  let allProducts = [];
  let cursor = null;
  let hasNextPage = true;
  while (hasNextPage) {
    const data = await shopifyFetch(query, { cursor });
    const edges = data.products.edges;
    allProducts = allProducts.concat(edges.map(({ node }) => normalizeProduct(node)));
    hasNextPage = data.products.pageInfo.hasNextPage;
    if (hasNextPage && edges.length > 0) {
      cursor = edges[edges.length - 1].cursor;
    }
  }
  return allProducts;
}

// Normalize Shopify product to our app format
function normalizeProduct(shopifyProduct) {
  const allImages = shopifyProduct.images.edges.map(({ node }) => node);
  const images = allImages.map(img => img.url);
  const price = Math.round(parseFloat(shopifyProduct.priceRange.minVariantPrice.amount));
  const variants = shopifyProduct.variants.edges.map(({ node }) => node);

  // Extract sizes and colors from ALL variants (not just available)
  const sizeSet = new Set();
  const colorSet = new Set();
  variants.forEach(v => {
    const parts = v.title.split('/').map(p => p.trim());
    if (parts.length > 1) {
      colorSet.add(parts[0]);
      sizeSet.add(parts[parts.length - 1]);
    } else {
      sizeSet.add(parts[0]);
    }
  });
  const sizes = [...sizeSet];
  const colors = [...colorSet];

  // Build color-to-images map using variant images
  // Each variant points to its front image; we find matching back images by URL suffix pattern
  const colorImages = {};
  const colorBackImages = {};
  const variantFrontUrls = {};

  // Get front image URL per color from variants
  variants.forEach(v => {
    const parts = v.title.split('/').map(p => p.trim());
    if (parts.length > 1 && v.image) {
      const color = parts[0];
      if (!variantFrontUrls[color]) {
        variantFrontUrls[color] = v.image.url;
      }
    }
  });

  // For each color's front image, try to find a matching back image
  // Pattern: front images have similar names to back images (e.g. same suffix number)
  const frontAltTexts = {};
  allImages.forEach(img => {
    if (img.altText) {
      Object.entries(variantFrontUrls).forEach(([color, frontUrl]) => {
        if (img.url === frontUrl) {
          frontAltTexts[color] = img.altText;
        }
      });
    }
  });

  Object.entries(variantFrontUrls).forEach(([color, frontUrl]) => {
    const frontAlt = frontAltTexts[color] || '';
    // Extract the suffix number from the front image filename
    const suffixMatch = frontAlt.match(/[_](\d+)\.\w+$/);
    const suffix = suffixMatch ? suffixMatch[1] : null;

    const colorImgs = [frontUrl];
    const seenUrls = new Set([frontUrl]);

    // Find back image with same suffix — use exact suffix match (_57. not just 57)
    if (suffix) {
      const suffixPattern = '_' + suffix + '.';
      const backImg = allImages.find(img =>
        img.altText &&
        img.altText.toLowerCase().includes('back') &&
        img.altText.includes(suffixPattern)
      );
      if (backImg) { colorImgs.push(backImg.url); seenUrls.add(backImg.url); colorBackImages[color] = backImg.url; }

      // Append any other images with the same suffix (extra shots for this color)
      allImages.forEach(img => {
        if (seenUrls.has(img.url)) return;
        if (img.altText && img.altText.includes(suffixPattern)) {
          const alt = img.altText.toLowerCase();
          const url = (img.url || '').toLowerCase();
          const isSizeChart = alt.includes('size') || alt.includes('chart') || url.includes('sizechart') || url.includes('size-chart') || url.includes('size_chart');
          if (!isSizeChart) {
            colorImgs.push(img.url);
            seenUrls.add(img.url);
          }
        }
      });
    }

    // Insert custom numbered images (alt text is just a number like "1", "2", "3")
    allImages.forEach(img => {
      if (seenUrls.has(img.url)) return;
      const alt = (img.altText || '').trim();
      const num = /^\d+$/.test(alt) ? parseInt(alt, 10) : null;
      if (num !== null && num > 0) {
        const pos = Math.min(num - 1, colorImgs.length);
        colorImgs.splice(pos, 0, img.url);
        seenUrls.add(img.url);
      }
    });

    colorImages[color] = colorImgs;
  });

  // Size chart: explicitly match alt text or URL filename containing size/chart
  const sizeChart = allImages.find(img => {
    const alt = (img.altText || '').toLowerCase();
    const url = (img.url || '').toLowerCase();
    return alt.includes('size') || alt.includes('chart') || url.includes('sizechart') || url.includes('size-chart') || url.includes('size_chart');
  });

  // Compare at price (original price before discount)
  const firstVariant = variants[0];
  const compareAtPrice = firstVariant?.compareAtPrice
    ? Math.round(parseFloat(firstVariant.compareAtPrice.amount))
    : null;

  // Determine categories from tags (product can be in multiple)
  const tags = shopifyProduct.tags.map(t => t.toLowerCase());
  const categories = [];
  if (tags.includes('music')) categories.push('music');
  if (tags.includes('games')) categories.push('games');
  if (tags.includes('rep')) categories.push('rep');
  if (tags.includes('goofy') || tags.includes('memes')) categories.push('goofy');
  if (categories.length === 0) categories.push('goofy');
  const category = categories[0];

  // Extract tag value — splits on first colon only, so "game:Halo 3: ODST" → "Halo 3: ODST"
  function getTagValue(prefix) {
    const tag = shopifyProduct.tags.find(t => t.toLowerCase().startsWith(prefix + ':'));
    return tag ? tag.substring(prefix.length + 1).trim() : null;
  }

  const genreTag = getTagValue('genre');
  const band = getTagValue('band');
  const genre = genreTag || (band && GENRE_MAP[band]) || null;
  const game = getTagValue('game');
  const brand = getTagValue('brand');

  const isDualPrint = shopifyProduct.title.toLowerCase().includes('dual print');
  const isFeatured = tags.includes('featured');
  const isFeaturedHome = tags.includes('featured-home');
  const featuredOrder = (() => {
    const fTag = tags.find(t => /^f\d+$/.test(t));
    return fTag ? parseInt(fTag.substring(1), 10) : 999;
  })();

  return {
    id: shopifyProduct.id,
    handle: shopifyProduct.handle,
    name: shopifyProduct.title,
    isDualPrint,
    isFeatured,
    isFeaturedHome,
    featuredOrder,
    price,
    compareAtPrice,
    category,
    categories,
    genre,
    band,
    game,
    brand,
    images,
    sizes: sizes.length > 0 ? sizes : ['S', 'M', 'L', 'XL', 'XXL'],
    colors: colors.length > 0 ? colors : null,
    defaultColor: (() => {
      // Find the color whose front image matches the first product image
      if (colors.length === 0) return null;
      const firstImageUrl = images[0];
      for (const [color, url] of Object.entries(variantFrontUrls)) {
        if (url === firstImageUrl) return color;
      }
      return colors[0];
    })(),
    colorImages,
    colorBackImages,
    sizeChart: sizeChart ? sizeChart.url : null,
    variants,
    description: shopifyProduct.description,
    shopifyId: shopifyProduct.id,
  };
}

// Create a Shopify cart
export async function createCart(lines) {
  const query = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
          lines(first: 50) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                  }
                }
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const cartLines = lines.map(line => ({
    merchandiseId: line.variantId,
    quantity: line.quantity,
  }));

  const data = await shopifyFetch(query, {
    input: { lines: cartLines },
  });

  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors[0].message);
  }

  return data.cartCreate.cart;
}

// Get variant ID by size for a product (handles "Color / Size" format)
export function getVariantBySize(product, size, color) {
  // Try matching both color and size first
  if (color) {
    const exact = product.variants.find(v => {
      const parts = v.title.split('/').map(p => p.trim());
      if (parts.length < 2) return false;
      return parts[0] === color && parts[parts.length - 1] === size && v.availableForSale;
    });
    if (exact) return exact.id;
  }
  // Fallback: match by size only
  const variant = product.variants.find(v => {
    const parts = v.title.split('/').map(p => p.trim());
    const variantSize = parts.length > 1 ? parts[parts.length - 1] : parts[0];
    return variantSize === size && v.availableForSale;
  });
  return variant ? variant.id : null;
}

export default shopifyFetch;
